import { captureException } from "@/observability/sentry";
import { formBackendUrlString } from "@/utils/formURL";

/**
 * The marketing site's contact form, delivered through the backend's public
 * `salesinquiry` endpoint. The endpoint is what holds the automation's hook,
 * checks reCAPTCHA and throttles by address; this side only sends what the
 * form collected, under the endpoint's own field names.
 */

/**
 * The three choices the form offers, by label, and the value each posts. The
 * endpoint passes the value through to the automation behind it, which was
 * built against the published form, so the values are that form's own,
 * spelling included: "5,000GB" is how its radio was valued.
 */
export const TRAFFIC = {
  "1,000 GB": "1,000 GB",
  "5,000 GB": "5,000GB",
  Unlimited: "Unlimited",
} as const;

export type TrafficLabel = keyof typeof TRAFFIC;

export type SalesInquiry = {
  fullName: string;
  companyName: string;
  email: string;
  phone: string;
  details: string;
  traffic: TrafficLabel;
};

const ENDPOINT = "/salesinquiry/";
/** Past the endpoint's own wait on the hook, which is ten seconds. */
const REQUEST_TIMEOUT_MS = 15_000;

/**
 * True once the endpoint has answered that the automation holds the inquiry.
 * False means the caller still holds it and must offer the reader another way
 * through, never that it was sent.
 */
export async function submitSalesInquiry(
  inquiry: SalesInquiry,
  recaptcha: string
): Promise<boolean> {
  try {
    const response = await fetch(formBackendUrlString(ENDPOINT), {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        full_name: inquiry.fullName.trim(),
        company_name: inquiry.companyName.trim(),
        company_email: inquiry.email.trim(),
        phone: inquiry.phone.trim(),
        details: inquiry.details.trim(),
        traffic: TRAFFIC[inquiry.traffic],
        recaptcha,
      }),
      signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    });
    if (!response.ok) {
      captureException(
        new Error(`Sales inquiry endpoint answered ${response.status}`)
      );
    }
    return response.ok;
  } catch (error) {
    captureException(error);
    return false;
  }
}
